import { AlertCircleIcon, BriefcaseIcon, DownloadIcon, HistoryIcon, MapPinIcon, PhoneIcon, PlusIcon, SearchIcon, SendIcon, UploadIcon, UsersIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Link, useSearchParams } from 'react-router';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { FigureBand } from '@/components/FigureBand';
import { JobMark } from '@/components/JobMark';
import { SectionTitle } from '@/components/PageHeader';
import { Pagination } from '@/components/Pagination';
import { JobStatusBadge, RoleBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { api, errorMessage } from '@/lib/api';
import { fmtNumber, fmtRelative } from '@/lib/format';
import { useMe } from '@/lib/auth';
import { jobAccent, jobIcon } from '@/lib/jobStyle';
import type { JobDto } from '@/lib/types';
import { withParam } from '@/lib/url';
import { cn } from '@/lib/utils';
import { JobsImportDialog } from './JobsImportDialog';
import { NewJobDialog } from './NewJobDialog';
import { useJobs } from './queries';

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5) return 'Working late';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/** "400 W Church St, Orlando, FL 32801" → "Orlando, FL"; anything shorter is shown as is. */
function shortPlace(address: string | null): string | null {
  if (!address) return null;
  const parts = address.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 3) return `${parts[parts.length - 2]}, ${parts[parts.length - 1]!.replace(/\s+\d{5}(-\d{4})?$/, '')}`;
  return parts.length === 2 ? parts.join(', ') : address;
}

/** Job cards per page; the grid is two or three across, so these keep whole rows. */
const JOB_PAGE_SIZES = [12, 24, 48];
const DEFAULT_JOB_PAGE_SIZE = 12;

/**
 * Home after sign-in: a greeting, the numbers that matter across the jobs this person can see,
 * then one card per job. Each card carries the job's look (cover photo or accent colour and icon),
 * its site, number, stats and the actions people actually reach for. A quick filter narrows the
 * list by name, description or site once there are more than a handful of jobs.
 */
export function JobsPage() {
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const includeArchived = params.get('archived') === '1';
  const jobs = useJobs(includeArchived);
  const newOpen = params.get('new') === '1' && me.permissions.createJobs;
  const [importOpen, setImportOpen] = useState(false);
  const [filter, setFilter] = useState('');
  // Cards come a page at a time once there are many jobs; the page and size live in the URL.
  const page = Math.max(1, Number(params.get('page')) || 1);
  const pageSize = JOB_PAGE_SIZES.includes(Number(params.get('size'))) ? Number(params.get('size')) : DEFAULT_JOB_PAGE_SIZE;
  const setPage = (p: number) => setParams((prev) => withParam(prev, 'page', p > 1 ? String(p) : null), { replace: true });
  const setPageSize = (s: number) => setParams((prev) => withParam(withParam(prev, 'size', s === DEFAULT_JOB_PAGE_SIZE ? null : String(s)), 'page', null), { replace: true });
  const today = new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'long', day: 'numeric' }).format(new Date());

  const setNewOpen = (open: boolean) => {
    const next = new URLSearchParams(params);
    if (open) next.set('new', '1');
    else next.delete('new');
    setParams(next, { replace: true });
  };

  const items = jobs.data?.items ?? [];
  const active = items.filter((j) => j.status === 'active');
  const totals = items.reduce(
    (acc, j) => ({ contacts: acc.contacts + (j.contactCount ?? 0), members: acc.members + (j.memberCount ?? 0), messages: acc.messages + (j.messageCount ?? 0) }),
    { contacts: 0, members: 0, messages: 0 },
  );
  const lastMessageAt = items.map((j) => j.lastMessageAt).filter((d): d is string => !!d).sort().at(-1) ?? null;
  const canSend = items.some((j) => j.myRole === 'admin');

  const q = filter.trim().toLowerCase();
  const visible = useMemo(() => (q ? items.filter((j) => [j.name, j.description ?? '', j.address ?? '', j.phoneNumber?.display ?? ''].some((s) => s.toLowerCase().includes(q))) : items), [items, q]);
  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize));
  const current = Math.min(page, pageCount);
  const shown = visible.slice((current - 1) * pageSize, current * pageSize);
  // A new filter starts over from the first page (the first render keeps a linked page).
  const lastQ = useRef(q);
  useEffect(() => {
    if (lastQ.current === q) return;
    lastQ.current = q;
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const subtitle = jobs.isPending
    ? 'Loading your jobs…'
    : items.length === 0
      ? me.permissions.createJobs
        ? 'Create your first job to get a phone number, load contacts and start sending.'
        : 'You have not been added to a job yet.'
      : me.permissions.createJobs
        ? `${fmtNumber(active.length)} active ${active.length === 1 ? 'job' : 'jobs'} across ${me.tenant.name}. Every job has its own number, contacts, history and team.`
        : `You are on ${fmtNumber(active.length)} ${active.length === 1 ? 'job' : 'jobs'}. ${canSend ? 'You can send messages on the jobs where you are an admin.' : 'You can manage contacts and follow delivery; job admins send the messages.'}`;

  return (
    <div className="space-y-10">
      <header className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          <p className="text-muted-foreground text-[11px] font-medium tracking-[0.16em] uppercase">{today}</p>
          <h1 className="font-display mt-2 text-4xl leading-none tracking-tight sm:text-5xl">
            {greeting()}, {me.user.firstName || me.user.fullName}.
          </h1>
          <p className="text-muted-foreground mt-3 max-w-xl text-sm leading-relaxed">{subtitle}</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <div role="radiogroup" aria-label="Which jobs" className="bg-card mr-2 inline-flex h-9 items-stretch border text-xs">
            {[
              { v: false, label: 'Active' },
              { v: true, label: 'All, with archived' },
            ].map((o) => (
              <button
                key={String(o.v)}
                type="button"
                role="radio"
                aria-checked={includeArchived === o.v}
                onClick={() => {
                  const next = new URLSearchParams(params);
                  if (o.v) next.set('archived', '1');
                  else next.delete('archived');
                  setParams(next, { replace: true });
                }}
                className={cn('border-r px-3 font-medium transition-colors last:border-r-0', includeArchived === o.v ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-accent hover:text-foreground')}
              >
                {o.label}
              </button>
            ))}
          </div>
          {me.permissions.createJobs && (
            <>
              <Button variant="outline" onClick={() => void api.jobs.exportXlsx().catch((e: unknown) => toast.error(errorMessage(e)))} title="Download every job (with its team) as an Excel workbook">
                <DownloadIcon /> Export
              </Button>
              <Button variant="outline" onClick={() => setImportOpen(true)} title="Create, update or archive jobs from a spreadsheet">
                <UploadIcon /> Import
              </Button>
              <Button onClick={() => setNewOpen(true)}>
                <PlusIcon /> New job
              </Button>
            </>
          )}
        </div>
      </header>

      {jobs.isError ? (
        <ErrorState error={jobs.error} onRetry={() => void jobs.refetch()} />
      ) : (
        <>
          <FigureBand
            ariaLabel="Across your jobs"
            figures={[
              { label: 'Active jobs', value: jobs.isPending ? undefined : fmtNumber(active.length), hint: items.length > active.length ? `${fmtNumber(items.length - active.length)} archived` : 'Each with its own number' },
              { label: 'Contacts', value: jobs.isPending ? undefined : fmtNumber(totals.contacts), hint: 'Across all your jobs' },
              { label: 'Team members', value: jobs.isPending ? undefined : fmtNumber(totals.members), hint: 'People assigned to jobs' },
              { label: 'Messages composed', value: jobs.isPending ? undefined : fmtNumber(totals.messages), hint: lastMessageAt ? `Last one ${fmtRelative(lastMessageAt)}` : 'Nothing sent yet' },
            ]}
          />

          <section>
            <SectionTitle
              actions={
                !jobs.isPending && items.length > 0 ? (
                  <div className="flex items-center gap-3">
                    {items.length > 3 && (
                      <div className="relative">
                        <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" aria-hidden />
                        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter jobs…" aria-label="Filter jobs" className="h-8 w-48 pl-8 text-sm sm:w-64" />
                      </div>
                    )}
                    <span className="text-muted-foreground text-xs tabular-nums">
                      {q ? `${fmtNumber(visible.length)} of ` : ''}
                      {fmtNumber(items.length)} {items.length === 1 ? 'job' : 'jobs'}
                    </span>
                  </div>
                ) : undefined
              }
            >
              Your jobs
            </SectionTitle>
            <div className="mt-4">
              {jobs.isPending ? (
                <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
                  {Array.from({ length: 2 }, (_, i) => (
                    <Skeleton key={i} className="h-72 w-full" />
                  ))}
                </div>
              ) : items.length === 0 ? (
                <EmptyState
                  icon={BriefcaseIcon}
                  title={me.permissions.createJobs ? 'No jobs yet' : 'You have not been assigned to a job'}
                  description={
                    me.permissions.createJobs
                      ? 'Create your first job to get a phone number, load contacts, and start sending.'
                      : 'Ask your organization administrator or a job admin to add you to a job.'
                  }
                  action={
                    me.permissions.createJobs ? (
                      <Button onClick={() => setNewOpen(true)}>
                        <PlusIcon /> Create a job
                      </Button>
                    ) : undefined
                  }
                />
              ) : visible.length === 0 ? (
                <EmptyState icon={SearchIcon} title="No jobs match" description={`Nothing matches “${filter.trim()}”.`} action={<Button variant="outline" onClick={() => setFilter('')}>Clear filter</Button>} className="py-12" />
              ) : (
                <>
                  <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
                    {shown.map((j, i) => (
                      <JobCard key={j.id} job={j} index={i} />
                    ))}
                  </div>
                  {visible.length > pageSize && <Pagination className="mt-4" page={current} pageSize={pageSize} total={visible.length} onPageChange={setPage} onPageSizeChange={setPageSize} pageSizes={JOB_PAGE_SIZES} />}
                </>
              )}
            </div>
          </section>
        </>
      )}

      {me.permissions.createJobs && <NewJobDialog open={newOpen} onOpenChange={setNewOpen} />}
      {me.permissions.createJobs && <JobsImportDialog open={importOpen} onOpenChange={setImportOpen} />}
    </div>
  );
}

function JobCard({ job, index }: { job: JobDto; index: number }) {
  const archived = job.status === 'archived';
  const isAdmin = job.myRole === 'admin';
  const accent = jobAccent(job);
  const Icon = jobIcon(job.icon);
  const place = shortPlace(job.address);
  // What still stops this job from sending; shown on the cover so it is seen before the first send.
  const gaps = archived ? [] : [!job.phoneNumber ? 'no number yet' : null, (job.contactCount ?? 0) === 0 ? 'no contacts yet' : null].filter((g): g is string => !!g);
  return (
    <article
      className={cn(
        'bg-card group relative flex flex-col border border-t-2 transition-colors',
        'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 fill-mode-both duration-500',
        archived ? 'opacity-80' : 'hover:border-foreground/30',
      )}
      style={{ animationDelay: `${index * 80}ms`, borderTopColor: accent }}
    >
      {/* Stretched link makes the whole card clickable; actions below sit above it. */}
      <Link to={`/jobs/${job.id}`} className="absolute inset-0 z-0 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50" aria-label={`Open ${job.name}`} />

      {/* Cover photo, or the accent with the job's icon watermarked. */}
      <div className="relative h-28 overflow-hidden" aria-hidden>
        {job.coverUrl ? (
          <img src={job.coverUrl} alt="" className={cn('size-full object-cover transition-transform duration-500', !archived && 'group-hover:scale-[1.03]', archived && 'grayscale')} />
        ) : (
          <div className="size-full" style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accent}B3 100%)` }}>
            <Icon className="absolute -right-3 -bottom-5 size-28 text-white/15" strokeWidth={1} />
          </div>
        )}
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {archived && <JobStatusBadge status={job.status} />}
          <RoleBadge role={job.myRole} />
        </div>
        {gaps.length > 0 && (
          <span className="bg-card/95 text-foreground absolute bottom-3 left-3 flex items-center gap-1.5 border px-2 py-1 text-[11px] font-medium shadow-sm" title="Finish setting up this job before the first send">
            <AlertCircleIcon className="text-warning size-3.5" aria-hidden /> Set up: {gaps.join(', ')}
          </span>
        )}
      </div>

      <div className="flex items-start gap-3.5 p-5 pb-4">
        <JobMark job={job} size="md" className="mt-0.5" />
        <div className="min-w-0 flex-1">
          <h3 className="font-display truncate text-2xl leading-none tracking-tight">{job.name}</h3>
          <p className="text-muted-foreground mt-2 line-clamp-2 min-h-[1.25rem] text-sm">{job.description ?? 'No description yet.'}</p>
          {place && (
            <p className="text-muted-foreground mt-2 flex items-center gap-1.5 text-xs">
              <MapPinIcon className="size-3.5 shrink-0" aria-hidden />
              <span className="truncate">{place}</span>
            </p>
          )}
        </div>
      </div>

      <dl className="grid grid-cols-3 border-t">
        <Stat label="Contacts" value={job.contactCount ?? 0} />
        <Stat label="Team" value={job.memberCount ?? 0} className="border-l" />
        <Stat label="Messages" value={job.messageCount ?? 0} className="border-l" />
      </dl>

      <div className="text-muted-foreground flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t px-5 py-3 text-xs">
        <span className="flex items-center gap-2 tabular-nums">
          <PhoneIcon className="size-3.5" aria-hidden />
          {job.phoneNumber?.display ?? 'No number assigned yet'}
        </span>
        <span>{job.lastMessageAt ? `Last message ${fmtRelative(job.lastMessageAt)}` : 'No messages yet'}</span>
      </div>

      <div className="bg-muted/30 relative z-10 mt-auto flex flex-wrap gap-2 border-t px-5 py-3">
        {isAdmin && !archived && (
          <Button asChild size="sm">
            <Link to={`/jobs/${job.id}/compose`}>
              <SendIcon /> Compose
            </Link>
          </Button>
        )}
        <Button asChild size="sm" variant="outline">
          <Link to={`/jobs/${job.id}/contacts`}>
            <UsersIcon /> Contacts
          </Link>
        </Button>
        <Button asChild size="sm" variant="ghost">
          <Link to={`/jobs/${job.id}/history`}>
            <HistoryIcon /> History
          </Link>
        </Button>
      </div>
    </article>
  );
}

function Stat({ label, value, className }: { label: string; value: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-0.5 px-5 py-3', className)}>
      <dt className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">{label}</dt>
      <dd className="text-lg font-semibold tracking-tight tabular-nums">{fmtNumber(value)}</dd>
    </div>
  );
}
